import { useState, useEffect, useMemo } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation } from "react-router-dom";
import { sidebarItems } from "./SideBarData";
import { useSelector } from "react-redux";
import { useGetNotificationsQuery } from "../../../redux/api/NotificationApi/notificationApi";
import { Badge } from "react-bootstrap";

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);

  const { data: notificationData } = useGetNotificationsQuery(undefined, {
    pollingInterval: 15000,
  });

  const unreadNotifications =
    notificationData?.notifications?.filter((n) => !n.isRead) || [];

  const appCount = unreadNotifications.filter(
    (n) => n.type === "NEW_APPLICATION",
  ).length;
  const contactCount = unreadNotifications.filter(
    (n) => n.type === "NEW_CONTACT" || n.type === "NEW_NEWSLETTER",
  ).length;

  const getBadgeCount = (module) => {
    if (module === "applications") return appCount;
    if (module === "contacts") return contactCount;
    return 0;
  };

  // Filter items based on user role and dynamic permissions
  const filteredSidebarItems = useMemo(() => {
    return sidebarItems.filter((item) => {
      // Superadmin always sees everything
      if (user?.role === "superadmin") return true;

      // Check for hardcoded role restrictions first
      if (item.roles && !item.roles.includes(user?.role)) {
        return false;
      }

      // Check for dynamic permissions (module-based access)
      // If user has a specific list of permissions, they can only see those modules
      if (user?.permissions && user.permissions.length > 0) {
        return user.permissions.includes(item.module);
      }

      // Default: If no specific permissions are set, return true (unless role restricted)
      return true;
    });
  }, [user]);

  const findSelectedKey = (items, path) => {
    for (const item of items) {
      if (item.url === path) {
        return item.id.toString();
      }
      if (item.children) {
        const childKey = findSelectedKey(item.children, path);
        if (childKey) {
          return childKey;
        }
      }
    }
    return null;
  };

  const selectedKey = findSelectedKey(filteredSidebarItems, location.pathname);

  useEffect(() => {
    const findParentKey = (items, path) => {
      for (const item of items) {
        if (item.url === path) {
          return item.id.toString();
        }
        if (item.children) {
          const childKey = findParentKey(item.children, path);
          if (childKey) {
            return item.id.toString();
          }
        }
      }
      return null;
    };

    const parentKey = findParentKey(filteredSidebarItems, location.pathname);
    if (parentKey) {
      setOpenKeys([parentKey]);
    }
  }, [location.pathname, filteredSidebarItems]);

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  return (
    <Sider
      width={200}
      theme="light"
      className="site-layout-background d-none d-lg-block"
      style={{
        borderRight: "2px solid #e9edf4",
        position: "fixed",
        top: "60px", // Approximate height of the header
        height: "calc(100vh - 70px)",
        overflowY: "auto",
        zIndex: 10,
        backgroundColor: "#ffffff",
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        style={{ height: "100%", borderRight: 0, paddingBottom: "80px" }}
        theme="light"
      >
        {filteredSidebarItems.map((menu) =>
          menu.children && menu.children.length > 0 ? (
            <SubMenu
              key={menu.id}
              icon={menu.icon}
              style={{
                color:
                  selectedKey === menu.id.toString() ||
                  menu.children.some(
                    (child) => selectedKey === child.id.toString(),
                  )
                    ? "var(--primary-color)"
                    : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id.toString() ||
                  menu.children.some(
                    (child) => selectedKey === child.id.toString(),
                  )
                    ? "#E5D6F3"
                    : "transparent",
              }}
              title={<span style={{ color: "#6d758a" }}>{menu.label}</span>}
            >
              {menu.children.map((item) => (
                <Menu.Item
                  key={item.id}
                  icon={item.icon}
                  style={{
                    color:
                      selectedKey === item.id.toString()
                        ? "var(--primary-color)"
                        : "#6d758a",
                    backgroundColor:
                      selectedKey === item.id.toString()
                        ? "#E5D6F3"
                        : "transparent",
                  }}
                >
                  <Link
                    className="link-unstyled text-decoration-none d-flex justify-content-between align-items-center w-100"
                    to={item.url}
                  >
                    <span>{item.label}</span>
                    {getBadgeCount(item.module) > 0 && (
                      <Badge pill bg="danger" style={{ fontSize: "10px" }}>
                        {getBadgeCount(item.module)}
                      </Badge>
                    )}
                  </Link>
                </Menu.Item>
              ))}
            </SubMenu>
          ) : (
            <Menu.Item
              key={menu.id}
              icon={menu.icon}
              style={{
                color:
                  selectedKey === menu.id.toString()
                    ? "var(--primary-color)"
                    : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id.toString()
                    ? "#e0dee2"
                    : "transparent",
                fontWeight: selectedKey === menu.id.toString() ? "600" : "",
              }}
            >
              <Link
                className="link-unstyled text-decoration-none d-flex justify-content-between align-items-center w-100"
                to={menu.url}
              >
                <span>{menu.label}</span>
                {getBadgeCount(menu.module) > 0 && (
                  <Badge pill bg="danger" style={{ fontSize: "10px" }}>
                    {getBadgeCount(menu.module)}
                  </Badge>
                )}
              </Link>
            </Menu.Item>
          ),
        )}
      </Menu>
    </Sider>
  );
};

export default Sidebar;
